import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTrophy, FaPlus, FaTrash, FaEdit, FaTimes, FaCheck,
  FaDice, FaSitemap, FaFlagCheckered, FaUsers, FaClock, FaCrown, FaMoneyBillWave, FaEye,
  FaExternalLinkAlt, FaRedo,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const FORMAT_OPTIONS = [
  { value: 'mini',     label: 'Mini - 1 poule x 4 joueurs',     max: 4  },
  { value: 'petit',    label: 'Petit - 2 poules x 4 joueurs',     max: 8  },
  { value: 'standard', label: 'Standard - 4 poules x 4 joueurs', max: 16 },
  { value: 'grand',    label: 'Grand - 8 poules x 4 joueurs',    max: 32 },
  { value: 'mundial',  label: 'Mondial - 12 poules x 4 joueurs', max: 48 },
];

const STATUS_MAP = {
  registration: { label: 'Inscriptions',   bg: 'bg-blue-500/20',  text: 'text-blue-400',  border: 'border-blue-500/30'  },
  group_stage:  { label: 'Phase de groupes', bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  knockout:     { label: 'Élimination directe', bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  finished:     { label: 'Terminé',         bg: 'bg-gray-500/20',  text: 'text-gray-400',  border: 'border-gray-500/30'  },
  cancelled:    { label: 'Annulé',          bg: 'bg-red-500/20',   text: 'text-red-400',   border: 'border-red-500/30'   },
};

const EMPTY_FORM = {
  nom: '', description: '', format: 'standard',
  prize_money: '', prize_description: '', prize_coins: '0',
  entry_fee: '0', entry_fee_coins: '0', premium_only: true, whatsapp_url: '',
  registration_deadline: '', group_stage_deadline: '', knockout_deadline: '',
};

function toForm(c) {
  const slice = (d) => (d ? new Date(d).toISOString().slice(0, 16) : '');
  return {
    nom:                   c.nom || '',
    description:           c.description || '',
    format:                c.format || 'standard',
    prize_money:           c.prizeMoney || '',
    prize_description:     c.prizeDescription || '',
    prize_coins:           String(c.prizeCoins ?? 0),
    entry_fee:             String(c.entryFee ?? 0),
    entry_fee_coins:       String(c.entryFeeCoins ?? 0),
    premium_only:          c.premiumOnly ?? true,
    whatsapp_url:          c.whatsappUrl || '',
    registration_deadline: slice(c.registrationDeadline),
    group_stage_deadline:  slice(c.groupStageDeadline),
    knockout_deadline:     slice(c.knockoutDeadline),
  };
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.registration;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-bold uppercase ${s.bg} ${s.text} ${s.border}`}
      style={{ fontFamily: "'Rajdhani', sans-serif" }}>
      {s.label}
    </span>
  );
}

function ParticipantsModal({ championship, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [resetting, setResetting]       = useState(null); // userId en cours

  const handleReset = async (userId, pseudo) => {
    if (!window.confirm(`Réinitialiser le quiz de ${pseudo} ? Il pourra rejouer sa phase de groupes.`)) return;
    setResetting(userId);
    try {
      const { data } = await api.delete(`/championships/${championship.id}/group-score/${userId}`);
      toast.success(data.message);
      // Mettre à jour localement
      setParticipants((prev) => prev.map((p) =>
        p.id === userId ? { ...p, score: null, correct_answers: null } : p
      ));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setResetting(null);
    }
  };

  useEffect(() => {
    api.get(`/championships/${championship.id}`)
      .then(({ data }) => setParticipants(data.participants || []))
      .catch(() => setParticipants([]))
      .finally(() => setLoading(false));
  }, [championship.id]);

  const isGroupStage = championship.status === 'group_stage' || championship.status === 'knockout' || championship.status === 'finished';
  const playedCount  = participants.filter((p) => p.score != null).length;

  // Organiser par groupe si tirage effectué
  const groupMap = {};
  const noGroup  = [];
  for (const p of participants) {
    if (p.group_number != null) {
      if (!groupMap[p.group_number]) groupMap[p.group_number] = [];
      groupMap[p.group_number].push(p);
    } else {
      noGroup.push(p);
    }
  }
  const hasGroups = Object.keys(groupMap).length > 0;

  // Trier chaque groupe par score décroissant
  for (const g of Object.keys(groupMap)) {
    groupMap[g].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: '#0d2b1a', border: '1px solid rgba(240,180,41,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <p className="text-qfl-gold font-black text-base uppercase" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {championship.nom}
            </p>
            <p className="text-white/40 text-xs">
              {participants.length} inscrit(s)
              {isGroupStage && ` · ${playedCount}/${participants.length} ont joué`}
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <FaTimes size={16} />
          </button>
        </div>

        {/* Barre de progression (phase de groupes) */}
        {isGroupStage && participants.length > 0 && (
          <div className="px-5 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-white/40" style={{ fontFamily: "'Rajdhani', sans-serif" }}>QUIZ DE GROUPE</span>
              <span className="font-bold" style={{
                fontFamily: "'Rajdhani', sans-serif",
                color: playedCount === participants.length ? '#22c55e' : '#f0b429',
              }}>
                {playedCount === participants.length ? '✅ TOUS ONT JOUÉ' : `${playedCount} / ${participants.length}`}
              </span>
            </div>
            <div className="w-full h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${participants.length > 0 ? (playedCount / participants.length) * 100 : 0}%`,
                  background: playedCount === participants.length
                    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                    : 'linear-gradient(90deg, #f0b429, #c97d08)',
                }}
              />
            </div>
          </div>
        )}

        {/* Contenu */}
        <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : participants.length === 0 ? (
            <div className="py-10 text-center text-white/30 text-sm">Aucun inscrit pour le moment</div>
          ) : hasGroups ? (
            // Vue par poule
            Object.entries(groupMap)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([g, players]) => (
                <div key={g}>
                  <div className="px-5 py-2 flex items-center justify-between"
                    style={{ background: 'rgba(240,180,41,0.06)', borderBottom: '1px solid rgba(240,180,41,0.1)' }}>
                    <span className="text-qfl-gold font-black text-xs uppercase tracking-widest"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      GROUPE {g}
                    </span>
                    <span className="text-white/30 text-xs">
                      {players.filter((p) => p.score != null).length}/{players.length} joué(s)
                    </span>
                  </div>
                  {players.map((p, i) => {
                    const hasPlayed = p.score != null;
                    const isQual    = i < 2 && hasPlayed;
                    return (
                      <div key={p.id} className="flex items-center gap-3 px-5 py-3"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span className="w-5 text-center font-black text-sm"
                          style={{ fontFamily: "'Rajdhani', sans-serif", color: isQual ? '#f0b429' : 'rgba(255,255,255,0.2)' }}>
                          {i + 1}
                        </span>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ background: 'rgba(240,180,41,0.12)', color: '#f0b429' }}>
                          {p.pseudo?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold truncate"
                            style={{ fontFamily: "'Rajdhani', sans-serif" }}>{p.pseudo}</p>
                          <p className="text-white/30 text-xs">Niv. {p.level}</p>
                        </div>
                        {hasPlayed ? (
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-qfl-gold font-black text-sm"
                                style={{ fontFamily: "'Rajdhani', sans-serif" }}>{p.score} pts</p>
                              <p className="text-white/30 text-xs">{p.correct_answers}/20</p>
                            </div>
                            <button
                              onClick={() => handleReset(p.id, p.pseudo)}
                              disabled={resetting === p.id}
                              title="Réinitialiser le quiz (rejouer)"
                              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
                              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
                              {resetting === p.id
                                ? <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                : <FaRedo size={10} />}
                            </button>
                          </div>
                        ) : (
                          <span className="text-white/25 text-xs italic">En attente…</span>
                        )}
                        {isQual && <span className="text-green-400 text-sm" title="Qualifié top 2">✓</span>}
                      </div>
                    );
                  })}
                </div>
              ))
          ) : (
            // Vue liste simple (pas encore de tirage)
            noGroup.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span className="w-5 text-center font-black text-xs text-white/25"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>{i + 1}</span>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: 'rgba(240,180,41,0.15)', color: '#f0b429' }}>
                  {p.pseudo?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}>{p.pseudo}</p>
                  <p className="text-white/30 text-xs">Niv. {p.level}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}

function AdminChampionships() {
  const navigate = useNavigate();
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [editingId, setEditingId]         = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [saving, setSaving]               = useState(false);
  const [acting, setActing]               = useState(null); // id en cours d'action
  const [viewingParticipants, setViewingParticipants] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/championships')
      .then(({ data }) => setChampionships(data.championships || []))
      .catch(() => setChampionships([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit   = (c) => { setEditingId(c.id); setForm(toForm(c)); setShowForm(true); };
  const closeForm  = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/championships/${editingId}`, form);
        toast.success('Tournoi mis à jour !');
      } else {
        await api.post('/championships', form);
        toast.success('Tournoi créé !');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (id, action, label, extra = {}) => {
    if (!window.confirm(`${label} ?`)) return;
    setActing(id + action);
    try {
      const { data } = await api.post(`/championships/${id}/${action}`, extra);
      toast.success(data.message || 'OK');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setActing(null);
    }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer "${nom}" ?`)) return;
    setActing(id + 'delete');
    try {
      await api.delete(`/championships/${id}`);
      toast.success('Tournoi supprimé');
      setChampionships((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black text-qfl-gold uppercase tracking-widest"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaTrophy className="inline mr-2 mb-1" />
            TOURNOIS
          </h2>
          <p className="text-white/50 text-sm mt-1">{championships.length} tournoi(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-qfl-gold text-qfl-dark font-bold text-sm hover:opacity-80 transition-opacity"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <FaPlus size={14} /> NOUVEAU
          </button>
          <button onClick={() => navigate('/admin/championships/create-mundial')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            🌍 MONDIAL
          </button>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="card mb-6" style={{ border: '1px solid rgba(240,180,41,0.3)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-qfl-gold font-bold uppercase tracking-widest text-sm"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {editingId ? 'Modifier le tournoi' : 'Créer un tournoi'}
            </h3>
            <button onClick={closeForm} className="text-white/40 hover:text-white/70"><FaTimes size={16} /></button>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nom */}
            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">Nom *</label>
              <input name="nom" value={form.nom} onChange={handleChange} required
                placeholder="Ex: Coupe de Mai 2026" className="form-input w-full" />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">Description</label>
              <input name="description" value={form.description} onChange={handleChange}
                placeholder="Ex: Tournoi mensuel avec prizes en argent réel" className="form-input w-full" />
            </div>

            {/* Format */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">Format *</label>
              <select name="format" value={form.format} onChange={handleChange} className="form-input w-full">
                <option value="mini">Mini - 1 poule x 4 joueurs</option>
                <option value="standard">Standard - 4 poules x 4 joueurs</option>
                <option value="grand">Grand - 8 poules x 4 joueurs</option>
                <option value="mundial">Mondial - 12 poules x 4 joueurs</option>
              </select>
            </div>

            {/* Entry fee */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">
                Frais d'entrée <span className="text-white/30">(FCFA, 0 = gratuit)</span>
              </label>
              <input type="number" name="entry_fee" value={form.entry_fee} onChange={handleChange}
                min="0" placeholder="0" className="form-input w-full" />
            </div>

            {/* Prize money */}
            <div>
              <label className="text-white/60 text-xs mb-1 block flex items-center gap-1">
                <FaMoneyBillWave size={11} className="text-green-400" /> Prize argent réel
              </label>
              <input name="prize_money" value={form.prize_money} onChange={handleChange}
                placeholder="Ex: 25 000 FCFA" className="form-input w-full" />
            </div>

            {/* Prize coins */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">Pièces virtuelles (1ère place)</label>
              <input type="number" name="prize_coins" value={form.prize_coins} onChange={handleChange}
                min="0" placeholder="0" className="form-input w-full" />
            </div>

            {/* Frais en pièces */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">
                Frais en pièces <span className="text-white/30">(0 = pas d'option pièces)</span>
              </label>
              <input type="number" name="entry_fee_coins" value={form.entry_fee_coins} onChange={handleChange}
                min="0" placeholder="0" className="form-input w-full" />
            </div>

            {/* Prize description */}
            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">Détail des prizes</label>
              <input name="prize_description" value={form.prize_description} onChange={handleChange}
                placeholder="Ex: 1er: 15 000F · 2e: 7 000F · 3e: 3 000F"
                className="form-input w-full" />
            </div>

            {/* Groupe WhatsApp */}
            <div className="md:col-span-2">
              <label className="text-white/60 text-xs mb-1 block">
                Lien du groupe WhatsApp <span className="text-white/30">(envoyé automatiquement à chaque inscrit)</span>
              </label>
              <input name="whatsapp_url" value={form.whatsapp_url} onChange={handleChange}
                placeholder="https://chat.whatsapp.com/..."
                className="form-input w-full" />
            </div>

            {/* Deadlines */}
            <div>
              <label className="text-white/60 text-xs mb-1 block">Fin des inscriptions</label>
              <input type="datetime-local" name="registration_deadline"
                value={form.registration_deadline} onChange={handleChange} className="form-input w-full" />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Fin phase de groupes</label>
              <input type="datetime-local" name="group_stage_deadline"
                value={form.group_stage_deadline} onChange={handleChange} className="form-input w-full" />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Fin phase éliminatoire</label>
              <input type="datetime-local" name="knockout_deadline"
                value={form.knockout_deadline} onChange={handleChange} className="form-input w-full" />
            </div>

            {/* Premium */}
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div onClick={() => setForm((f) => ({ ...f, premium_only: !f.premium_only }))}
                  className={`w-12 h-6 rounded-full relative transition-colors ${form.premium_only ? 'bg-qfl-gold' : 'bg-white/20'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.premium_only ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <FaCrown className="text-qfl-gold" size={12} /> Réservé Premium
                </span>
              </label>
            </div>

            <div className="md:col-span-2 flex gap-3 justify-end mt-2">
              <button type="button" onClick={closeForm}
                className="px-4 py-2 rounded-xl bg-white/10 text-white/60 text-sm font-bold hover:bg-white/20 transition-colors"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Annuler
              </button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-qfl-gold text-qfl-dark font-bold text-sm hover:opacity-80 disabled:opacity-50 transition-opacity"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {saving
                  ? <div className="w-4 h-4 border-2 border-qfl-dark border-t-transparent rounded-full animate-spin" />
                  : <FaCheck size={14} />}
                {editingId ? 'ENREGISTRER' : 'CRÉER'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : championships.length === 0 ? (
        <div className="text-center py-16">
          <FaTrophy size={40} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">Aucun tournoi. Créez-en un !</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {championships.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }} className="card flex items-center gap-4">
              {/* Icône format */}
              <div className="w-10 h-10 rounded-xl bg-qfl-gold/10 border border-qfl-gold/20 flex items-center justify-center flex-shrink-0">
                <span style={{ fontSize: 18 }}>
                  {c.format === 'mini' ? '🏅' : c.format === 'grand' ? '🏆' : '⚽'}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="font-bold text-white text-sm truncate"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}>{c.nom}</span>
                  <StatusBadge status={c.status} />
                  {c.premiumOnly && <FaCrown size={11} className="text-qfl-gold" />}
                </div>
                <div className="flex items-center gap-3 text-white/40 text-xs flex-wrap">
                  <span className="flex items-center gap-1">
                    <FaUsers size={9} /> {c.participantCount}/{c.maxPlayers}
                  </span>
                  <span className="text-white/30">{c.formatLabel}</span>
                  {c.prizeMoney && (
                    <span className="flex items-center gap-1 text-green-400">
                      <FaMoneyBillWave size={9} /> {c.prizeMoney}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Tirage au sort (registration → group_stage) */}
                {c.status === 'registration' && (
                  <button
                    onClick={() => handleAction(c.id, 'draw', `Lancer le tirage au sort et démarrer la phase de groupes de "${c.nom}"`)}
                    disabled={acting === c.id + 'draw'}
                    className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                    title="Tirage au sort & lancer phase de groupes">
                    {acting === c.id + 'draw'
                      ? <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      : <FaDice size={13} />}
                  </button>
                )}

                {/* Construire le bracket (group_stage → knockout) */}
                {c.status === 'group_stage' && (
                  <button
                    onClick={() => handleAction(c.id, 'build-bracket', `Clôturer la phase de groupes et construire le bracket de "${c.nom}"`)}
                    disabled={acting === c.id + 'build-bracket'}
                    className="w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 hover:bg-orange-500/20 transition-colors disabled:opacity-50"
                    title="Construire le bracket éliminatoire">
                    {acting === c.id + 'build-bracket'
                      ? <div className="w-3.5 h-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                      : <FaSitemap size={13} />}
                  </button>
                )}

                {/* Clôturer (knockout → finished) */}
                {c.status === 'knockout' && (
                  <button
                    onClick={() => handleAction(c.id, 'close', `Clôturer définitivement le tournoi "${c.nom}"`)}
                    disabled={acting === c.id + 'close'}
                    className="w-9 h-9 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                    title="Clôturer le tournoi">
                    {acting === c.id + 'close'
                      ? <div className="w-3.5 h-3.5 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                      : <FaFlagCheckered size={12} />}
                  </button>
                )}

                {/* Modifier */}
                <button onClick={() => openEdit(c)}
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/10 transition-colors"
                  title="Modifier">
                  <FaEdit size={13} />
                </button>

                {/* Voir la page tournoi (poules / bracket) */}
                <button
                  onClick={() => navigate(`/championships/${c.id}`)}
                  className="w-9 h-9 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 hover:bg-green-500/20 transition-colors"
                  title="Voir les poules / bracket">
                  <FaExternalLinkAlt size={11} />
                </button>

                {/* Voir les inscrits */}
                <button
                  onClick={() => setViewingParticipants(c)}
                  className="w-9 h-9 rounded-lg bg-qfl-gold/10 border border-qfl-gold/20 flex items-center justify-center text-qfl-gold hover:bg-qfl-gold/20 transition-colors"
                  title="Voir les inscrits">
                  <FaEye size={12} />
                </button>

                {/* Supprimer */}
                <button onClick={() => handleDelete(c.id, c.nom)}
                  disabled={acting === c.id + 'delete'}
                  className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  title="Supprimer">
                  {acting === c.id + 'delete'
                    ? <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    : <FaTrash size={12} />}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {viewingParticipants && (
          <ParticipantsModal
            championship={viewingParticipants}
            onClose={() => setViewingParticipants(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminChampionships;
